/**
 * Utilidades de validación para formularios
 *
 * @description Validaciones reutilizables para campos de formularios
 * según los estándares de seguridad del proyecto.
 * @module utils/formValidation
 */

/**
 * Regex para validación de email según RFC 5322 (simplificado pero más estricto)
 * Acepta: usuario@dominio.ext
 * Rechaza: dominios con puntos consecutivos, sin usuario, etc.
 * @type {RegExp}
 */
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

/**
 * Longitud máxima permitida para campos de texto según reglas de seguridad
 * @type {number}
 */
export const MAX_TEXT_LENGTH = 1000

/**
 * Longitud máxima para nombre de entidad financiera
 * @type {number}
 */
export const MAX_BANK_ENTITY_NAME_LENGTH = 100

/**
 * Regex para nombre de entidad financiera: letras, números, espacios y . & ' -
 * @type {RegExp}
 */
export const BANK_ENTITY_NAME_REGEX = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\s.&'-]+$/

/**
 * Regex para RUC de entidad financiera: 11 dígitos con prefijo 20
 * @type {RegExp}
 */
export const FINANCIAL_ENTITY_RUC_REGEX = /^20\d{9}$/

/**
 * Monto mínimo permitido para endoso bancario (soles)
 * @type {number}
 */
export const MIN_ENDORSEMENT_AMOUNT = 1

/**
 * Regex para monto a endosar: entero o hasta 2 decimales
 * @type {RegExp}
 */
export const ENDORSEMENT_AMOUNT_FORMAT_REGEX = /^\d+(\.\d{1,2})?$/

/**
 * Caracteres permitidos en correo electrónico
 * @type {RegExp}
 */
export const EMAIL_CHAR_REGEX = /[a-zA-Z0-9@._%+-]/

/**
 * Valida estructura estricta del correo (sin puntos consecutivos ni etiquetas vacías)
 *
 * @param {string} email - Correo ya recortado
 * @returns {boolean} - true si la estructura es inválida
 */
function hasInvalidEmailStructure (email) {
  if (/\.\./.test(email)) {
    return true
  }

  if (/\.@|@\./.test(email)) {
    return true
  }

  const parts = email.split('@')
  if (parts.length !== 2) {
    return true
  }

  const [localPart, domainPart] = parts
  if (!localPart || !domainPart) {
    return true
  }

  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return true
  }

  if (domainPart.startsWith('.') || domainPart.endsWith('.')) {
    return true
  }

  const domainLabels = domainPart.split('.')
  if (domainLabels.some((label) => !label)) {
    return true
  }

  return false
}

/**
 * Sanitiza entrada de correo electrónico eliminando espacios y caracteres no permitidos
 *
 * @param {string} value - Texto ingresado
 * @returns {string} - Correo sanitizado
 */
export function sanitizeEmailInput (value) {
  if (!value || typeof value !== 'string') {
    return ''
  }

  return value.replace(/\s/g, '').replace(/[^a-zA-Z0-9@._%+-]/g, '').substring(0, 100)
}

/**
 * Sanitiza nombre de entidad financiera eliminando caracteres no permitidos
 *
 * @param {string} value - Texto ingresado
 * @returns {string} - Nombre sanitizado
 */
export function sanitizeBankEntityName (value) {
  if (!value || typeof value !== 'string') {
    return ''
  }

  return value
    .replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\s.&'-]/g, '')
    .substring(0, MAX_BANK_ENTITY_NAME_LENGTH)
}

/**
 * Valida formato de correo electrónico
 *
 * @param {string} email - Correo electrónico a validar
 * @returns {Object} - { isValid: boolean, errors: Array<string> }
 *
 * @example
 * const { isValid, errors } = validateEmail('user@example.com')
 * if (!isValid) {
 *   console.log('Errores:', errors)
 * }
 */
export function validateEmail (email) {
  const errors = []

  if (!email) {
    errors.push('El correo electrónico es requerido')
    return { isValid: false, errors }
  }

  if (typeof email !== 'string') {
    errors.push('El correo electrónico debe ser texto')
    return { isValid: false, errors }
  }

  const trimmedEmail = email.trim()

  if (trimmedEmail.length === 0) {
    errors.push('El correo electrónico no puede estar vacío')
  }

  if (trimmedEmail.length > 100) {
    errors.push('El correo electrónico no puede tener más de 100 caracteres')
  }

  if (!EMAIL_REGEX.test(trimmedEmail) || hasInvalidEmailStructure(trimmedEmail)) {
    errors.push('El formato del correo electrónico no es válido')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Valida longitud de texto según límites de seguridad
 *
 * @param {string} text - Texto a validar
 * @param {number} maxLength - Longitud máxima permitida (default: 1000)
 * @param {string} fieldName - Nombre del campo para mensajes de error
 * @returns {Object} - { isValid: boolean, errors: Array<string> }
 *
 * @example
 * const { isValid, errors } = validateTextLength(motivo, 1000, 'Motivo de anulación')
 */
export function validateTextLength (text, maxLength = MAX_TEXT_LENGTH, fieldName = 'Campo') {
  const errors = []

  if (!text) {
    return { isValid: true, errors }
  }

  if (typeof text !== 'string') {
    errors.push(`${fieldName} debe ser texto`)
    return { isValid: false, errors }
  }

  const trimmedText = text.trim()

  if (trimmedText.length > maxLength) {
    errors.push(`${fieldName} no puede tener más de ${maxLength} caracteres`)
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Valida que un valor esté dentro de una lista permitida
 *
 * @param {*} value - Valor a validar
 * @param {Array} allowedValues - Lista de valores permitidos
 * @param {string} fieldName - Nombre del campo para mensajes de error
 * @returns {Object} - { isValid: boolean, errors: Array<string> }
 *
 * @example
 * const { isValid, errors } = validateInList(reason, SOAT_CANCELLATION_REASONS, 'Motivo')
 */
export function validateInList (value, allowedValues = [], fieldName = 'Campo') {
  const errors = []

  if (!value) {
    errors.push(`${fieldName} es requerido`)
    return { isValid: false, errors }
  }

  if (!Array.isArray(allowedValues)) {
    errors.push('Lista de valores permitidos inválida')
    return { isValid: false, errors }
  }

  if (!allowedValues.includes(value)) {
    errors.push(`${fieldName} no es válido`)
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Valida número de póliza
 *
 * @param {string} policyNumber - Número de póliza a validar
 * @returns {Object} - { isValid: boolean, errors: Array<string> }
 *
 * @example
 * const { isValid, errors } = validatePolicyNumber('123456789')
 */
export function validatePolicyNumber (policyNumber) {
  const errors = []

  if (!policyNumber) {
    errors.push('El número de póliza es requerido')
    return { isValid: false, errors }
  }

  if (typeof policyNumber !== 'string') {
    errors.push('El número de póliza debe ser texto')
    return { isValid: false, errors }
  }

  const trimmedPolicy = policyNumber.trim()

  if (trimmedPolicy.length === 0) {
    errors.push('El número de póliza no puede estar vacío')
  }

  if (trimmedPolicy.length < 5) {
    errors.push('El número de póliza debe tener al menos 5 caracteres')
  }

  if (trimmedPolicy.length > 50) {
    errors.push('El número de póliza no puede tener más de 50 caracteres')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Sanitiza texto básico (trim y límite de longitud)
 *
 * @param {string} text - Texto a sanitizar
 * @param {number} maxLength - Longitud máxima (default: 1000)
 * @returns {string} - Texto sanitizado
 *
 * @example
 * const cleanText = sanitizeText(userInput, 1000)
 */
export function sanitizeText (text, maxLength = MAX_TEXT_LENGTH) {
  if (!text || typeof text !== 'string') {
    return ''
  }

  return text.trim().substring(0, maxLength)
}

/**
 * Valida nombre de entidad financiera para endoso bancario
 *
 * @param {string} name - Nombre de la entidad financiera
 * @returns {Object} - { isValid: boolean, errors: Array<string> }
 */
export function validateBankEntityName (name) {
  const errors = []

  if (!name || typeof name !== 'string') {
    errors.push('El nombre de la entidad financiera es requerido')
    return { isValid: false, errors }
  }

  const trimmedName = name.trim()

  if (trimmedName.length === 0) {
    errors.push('El nombre de la entidad financiera es requerido')
    return { isValid: false, errors }
  }

  if (trimmedName.length > MAX_BANK_ENTITY_NAME_LENGTH) {
    errors.push(`El nombre no puede tener más de ${MAX_BANK_ENTITY_NAME_LENGTH} caracteres`)
  }

  if (!BANK_ENTITY_NAME_REGEX.test(trimmedName)) {
    errors.push('El nombre solo puede contener letras, números y los caracteres . & \' -')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Valida RUC de entidad financiera: 11 dígitos numéricos con prefijo 20
 *
 * @param {string} ruc - RUC de la entidad financiera
 * @returns {Object} - { isValid: boolean, errors: Array<string> }
 */
export function validateFinancialEntityRuc (ruc) {
  const errors = []

  if (!ruc || typeof ruc !== 'string') {
    errors.push('El RUC es requerido')
    return { isValid: false, errors }
  }

  const trimmedRuc = ruc.trim()

  if (trimmedRuc.length === 0) {
    errors.push('El RUC es requerido')
    return { isValid: false, errors }
  }

  if (!/^\d+$/.test(trimmedRuc)) {
    errors.push('El RUC debe contener solo números')
    return { isValid: false, errors }
  }

  if (!FINANCIAL_ENTITY_RUC_REGEX.test(trimmedRuc)) {
    errors.push('El RUC debe tener 11 dígitos numéricos y comenzar con 20')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Valida monto a endosar: mínimo S/ 1, hasta 2 decimales y tope máximo opcional
 *
 * @param {string} amount - Monto ingresado
 * @param {number|null|undefined} maxAmount - Tope máximo disponible (endosos.montoMaximo)
 * @returns {Object} - { isValid: boolean, errors: Array<string> }
 */
export function validateEndorsementAmount (amount, maxAmount) {
  const errors = []

  if (!amount || typeof amount !== 'string') {
    errors.push('El monto a endosar es requerido')
    return { isValid: false, errors }
  }

  const trimmedAmount = amount.trim()

  if (trimmedAmount.length === 0) {
    errors.push('El monto a endosar es requerido')
    return { isValid: false, errors }
  }

  if (!ENDORSEMENT_AMOUNT_FORMAT_REGEX.test(trimmedAmount)) {
    errors.push('Debe ingresar un monto válido')
    return { isValid: false, errors }
  }

  const numericAmount = parseFloat(trimmedAmount)

  if (isNaN(numericAmount) || numericAmount < MIN_ENDORSEMENT_AMOUNT) {
    errors.push('El monto mínimo a endosar es S/ 1.00')
    return { isValid: false, errors }
  }

  if (maxAmount != null && !isNaN(maxAmount) && numericAmount > maxAmount) {
    errors.push('El monto no puede superar el máximo disponible')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}



// WEBPACK FOOTER //
// ./src/utils/formValidation.js